/** Labels, categories, and filters for campaign activity events. */

export const ACTIVITY_CATEGORY_OPTIONS = [
  { value: "all", label: "All categories" },
  { value: "campaign", label: "Campaign lifecycle" },
  { value: "enrollment", label: "Prospects & leads" },
  { value: "outreach", label: "Outreach steps" },
  { value: "setup", label: "Setup & config" },
];

export const ACTIVITY_DATE_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

/** Calendar-day span per range; today counts as day 1 so "7d" covers today plus six days. */
const ACTIVITY_RANGE_DAYS = { today: 1, "7d": 7, "30d": 30 };

/** Always listed in the event-type filter with readable labels. */
export const ACTIVITY_PRIMARY_EVENT_TYPES = [
  "campaign_started",
  "campaign_resumed",
  "campaign_activated",
  "campaign_paused",
  "wait_scheduled",
  "enrollment_exited",
];

const EVENT_LABELS = {
  campaign_started: "Campaign started",
  campaign_paused: "Campaign paused",
  campaign_resumed: "Campaign resumed",
  campaign_activated: "Campaign activated",
  campaign_cancelled: "Campaign cancelled",
  campaign_archived: "Campaign archived",
  campaign_created: "Campaign created",
  campaign_updated: "Campaign updated",
  campaign_deleted: "Campaign deleted",
  campaign_duplicated: "Campaign duplicated",
  workflow_published: "Workflow published",
  enrollment_exited: "Prospect exited",
  enrollment_completed: "Prospect completed sequence",
  lead_status_changed: "Lead status updated",
  reply_received: "Reply received",
  wait_scheduled: "Wait step scheduled",
  message_sent: "Message sent",
  message_queued: "Message queued",
  step_skipped: "Step skipped",
  step_failed: "Step failed",
  channels_assigned: "Channels assigned",
  ai_sequence_designed: "AI sequence designed",
  campaign_default_set: "Set as default campaign",
  campaign_default_cleared: "Default campaign cleared",
  lead_owner_notified: "Lead owner notified",
};

const EVENT_CATEGORIES = {
  campaign: new Set([
    "campaign_started",
    "campaign_paused",
    "campaign_resumed",
    "campaign_activated",
    "campaign_cancelled",
    "campaign_archived",
  ]),
  enrollment: new Set([
    "enrollment_exited",
    "enrollment_completed",
    "lead_status_changed",
    "reply_received",
    "lead_owner_notified",
  ]),
  outreach: new Set([
    "message_sent",
    "message_queued",
    "wait_scheduled",
    "step_skipped",
    "step_failed",
  ]),
  setup: new Set([
    "campaign_created",
    "campaign_updated",
    "campaign_deleted",
    "campaign_duplicated",
    "workflow_published",
    "channels_assigned",
    "ai_sequence_designed",
    "campaign_default_set",
    "campaign_default_cleared",
  ]),
};

const CATEGORY_META = {
  campaign: { label: "Lifecycle", className: "bg-indigo-50 text-indigo-700 ring-indigo-100" },
  enrollment: { label: "Prospect", className: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
  lead: { label: "Lead", className: "bg-amber-50 text-amber-800 ring-amber-100" },
  outreach: { label: "Outreach", className: "bg-sky-50 text-sky-700 ring-sky-100" },
  setup: { label: "Setup", className: "bg-slate-100 text-slate-600 ring-slate-200" },
  other: { label: "Event", className: "bg-slate-100 text-slate-600 ring-slate-200" },
};

/** Badge prefix can be finer-grained than the filter category (e.g. Lead vs Prospect). */
const EVENT_BADGE_KEYS = {
  lead_status_changed: "lead",
  lead_owner_notified: "lead",
};

const LEAD_STATUS_LABELS = {
  new: "New",
  in_progress: "In progress",
  qualified_inquiry: "Qualified (inquiry)",
  qualified_mature: "Qualified (mature)",
  unqualified_inquiry: "Unqualified (inquiry)",
  unqualified_mature: "Unqualified (mature)",
};

const CHANNEL_LABELS = {
  email: "Email",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
  sms: "SMS",
  telegram: "Telegram",
  voice: "AI voice",
};

function humanizeToken(value) {
  if (!value) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function leadStatusLabel(value) {
  if (!value) return "Unknown";
  return LEAD_STATUS_LABELS[value] || humanizeToken(value);
}

function channelLabel(value) {
  if (!value) return "";
  return CHANNEL_LABELS[value] || humanizeToken(value);
}

export function getActivityEventCategory(eventType) {
  const key = String(eventType || "");
  for (const [category, types] of Object.entries(EVENT_CATEGORIES)) {
    if (types.has(key)) return category;
  }
  return "other";
}

export function getActivityCategoryMeta(eventType) {
  const key = String(eventType || "");
  const badgeKey = EVENT_BADGE_KEYS[key] || getActivityEventCategory(key);
  return CATEGORY_META[badgeKey] || CATEGORY_META.other;
}

export function formatActivityEventLabel(eventType) {
  const key = String(eventType || "");
  return EVENT_LABELS[key] || humanizeToken(key);
}

function formatTimestamp(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

export function formatActivityEventDescription(event) {
  const type = event?.event_type;
  const payload = event?.payload && typeof event.payload === "object" ? event.payload : {};

  switch (type) {
    case "campaign_started":
      if (payload.scheduled_start_at) {
        return `Scheduled to start ${formatTimestamp(payload.scheduled_start_at)}`;
      }
      if (payload.enrollments_activated != null) {
        return `${payload.enrollments_activated} prospect(s) activated`;
      }
      return "Campaign execution started";
    case "campaign_paused":
      return payload.paused != null
        ? `${payload.paused} active prospect(s) paused`
        : "Campaign paused for all active prospects";
    case "campaign_resumed":
      return payload.resumed != null
        ? `${payload.resumed} prospect(s) resumed`
        : "Campaign resumed for paused prospects";
    case "campaign_activated":
      return payload.enrolled != null
        ? `Campaign activated · ${payload.enrolled} prospect(s) enrolled`
        : "Campaign activated";
    case "campaign_cancelled":
      return payload.enrollments_cancelled != null
        ? `${payload.enrollments_cancelled} prospect(s) cancelled`
        : "Campaign cancelled";
    case "campaign_archived":
      return "Campaign archived · existing prospects continue";
    case "workflow_published":
      if (payload.version) return `Published workflow ${payload.version}`;
      if (Array.isArray(payload.warnings) && payload.warnings.length) {
        return `Published with ${payload.warnings.length} warning(s)`;
      }
      return "Workflow published to active prospects";
    case "campaign_updated":
      if (payload.action === "save") return "Campaign wizard progress saved";
      if (Array.isArray(payload.fields) && payload.fields.length) {
        return `Updated ${payload.fields.join(", ")}`;
      }
      if (payload.step != null) return `Wizard step ${payload.step} saved`;
      return "Campaign settings updated";
    case "campaign_created":
      return payload.name ? `Created "${payload.name}"` : "New campaign created";
    case "campaign_duplicated":
      return payload.source_id
        ? `Duplicated from campaign ${String(payload.source_id).slice(0, 8)}…`
        : "Campaign duplicated";
    case "campaign_deleted":
      return "Campaign removed";
    case "channels_assigned":
      if (Array.isArray(payload.channels) && payload.channels.length) {
        return `Assigned ${payload.channels.map(channelLabel).join(", ")}`;
      }
      return "Outreach channels assigned";
    case "ai_sequence_designed":
      return payload.summary || "AI-generated sequence applied";
    case "enrollment_exited": {
      const name = payload.display_name || "Prospect";
      const reason = humanizeToken(payload.reason || "manual exit");
      return `${name} exited · ${reason}`;
    }
    case "enrollment_completed":
      return payload.reason
        ? `Sequence completed · ${humanizeToken(payload.reason)}`
        : "Prospect finished the sequence";
    case "lead_status_changed":
      return `${leadStatusLabel(payload.from)} → ${leadStatusLabel(payload.to)}${
        payload.qualification_source
          ? ` · ${payload.qualification_source}`
          : payload.reason
            ? ` · ${humanizeToken(payload.reason)}`
            : ""
      }`;
    case "reply_received":
      return payload.channel
        ? `Reply on ${channelLabel(payload.channel)}${payload.from ? ` from ${payload.from}` : ""}`
        : "Inbound reply received";
    case "wait_scheduled":
      return payload.until
        ? `Waiting until ${formatTimestamp(payload.until)}`
        : "Wait step scheduled";
    case "message_queued":
      return payload.channel
        ? `${channelLabel(payload.channel)} message queued${
          payload.recipient ? ` for ${payload.recipient}` : ""
        }`
        : "Outreach message queued";
    case "message_sent":
      return payload.channel
        ? `${channelLabel(payload.channel)} sent${
          payload.recipient ? ` to ${payload.recipient}` : ""
        }`
        : "Outreach message delivered";
    case "step_skipped":
      return payload.reason
        ? `Step skipped · ${humanizeToken(payload.reason)}`
        : "Workflow step skipped";
    case "step_failed":
      return payload.error || payload.reason
        ? `Step failed · ${payload.error || humanizeToken(payload.reason)}`
        : "Workflow step failed";
    case "lead_owner_notified":
      return payload.lead_status
        ? `Owner notified · ${leadStatusLabel(payload.lead_status)}`
        : "Lead owner notified";
    case "campaign_default_set":
      return "Marked as default campaign for assigned channels";
    case "campaign_default_cleared":
      return "Default campaign assignment cleared";
    default:
      return null;
  }
}

function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Start of the selected range in the viewer's timezone, or null for "all time". */
export function activityRangeStart(dateRange, now = new Date()) {
  const days = ACTIVITY_RANGE_DAYS[dateRange];
  if (!days) return null;
  const start = startOfLocalDay(now);
  start.setDate(start.getDate() - (days - 1));
  return start;
}

/** Cutoff sent to the API so the range applies to every stored event, not just the fetched page. */
export function activityRangeSince(dateRange, now = new Date()) {
  const start = activityRangeStart(dateRange, now);
  return start ? start.toISOString() : null;
}

function matchesDateFilter(createdAt, dateFilter) {
  const start = activityRangeStart(dateFilter);
  if (!start) return true;
  if (!createdAt) return false;
  const eventDate = new Date(createdAt);
  if (Number.isNaN(eventDate.getTime())) return false;
  return eventDate >= start;
}

export function buildActivityEventTypeOptions(events) {
  const fromData = (events || []).map((ev) => ev.event_type).filter(Boolean);
  const merged = [
    ...ACTIVITY_PRIMARY_EVENT_TYPES,
    ...fromData.filter((type) => !ACTIVITY_PRIMARY_EVENT_TYPES.includes(type)),
  ];
  const unique = [...new Set(merged)];
  return [
    { value: "all", label: "All event types" },
    ...unique.map((type) => ({
      value: type,
      label: formatActivityEventLabel(type),
    })),
  ];
}

export function filterCampaignActivity(events, {
  category = "all",
  eventType = "all",
  dateRange = "all",
  enrollmentOnly = false,
} = {}) {
  return (events || []).filter((ev) => {
    if (enrollmentOnly && !ev.enrollment_id) return false;
    if (category !== "all" && getActivityEventCategory(ev.event_type) !== category) return false;
    if (eventType !== "all" && ev.event_type !== eventType) return false;
    if (!matchesDateFilter(ev.created_at, dateRange)) return false;
    return true;
  });
}

export function groupActivityByDate(events) {
  const groups = [];
  const indexByKey = new Map();

  for (const ev of events || []) {
    const date = ev.created_at ? new Date(ev.created_at) : null;
    const key = date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
      : "Unknown date";

    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({ key, events: [] });
    }
    groups[indexByKey.get(key)].events.push(ev);
  }

  return groups;
}
